import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type {
  BlockedOtcInvoiceQueueEntry,
  OtcInvoiceQueueListItem,
  OtcInvoiceSendBlockingReason,
  SendOtcInvoiceQueueEntriesResult,
} from "@/lib/api/otcInvoices";
import { getAmendmentTypeDisplay } from "@/section/otc/contract-form-v3/amendmentTypes";

function prettifyCode(code: string): string {
  return code
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function BlockingReasonsDialog({
  entry,
  customerName,
  onClose,
}: {
  entry: OtcInvoiceQueueListItem | null;
  customerName: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={entry !== null} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Invoice Queue Blocking Reasons</DialogTitle>
          <DialogDescription>
            Contract {entry?.contract_number || "—"}, Layer {entry?.layer_no ?? "—"}, {customerName || "Unknown customer"}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {entry?.send_blocking_reasons.length === 0 && (
            <p className="text-sm text-muted-foreground">This queue entry has no current blocking reasons.</p>
          )}
          {entry?.send_blocking_reasons.map((reason: OtcInvoiceSendBlockingReason, index: number) => (
            <div key={`${reason.code}-${index}`} className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="font-medium text-red-900">{reason.message}</p>
              <p className="mt-1 text-xs text-red-700">{prettifyCode(reason.code)}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReasonDialog({
  open,
  title,
  description,
  submitLabel,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  description: string;
  submitLabel: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const trimmedReason = reason.trim();

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen && !isSubmitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="otc-invoice-action-reason">Reason</Label>
          <Textarea
            id="otc-invoice-action-reason"
            value={reason}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setReason(event.target.value)}
            placeholder="Enter a reason"
            disabled={isSubmitting}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="button" onClick={() => onSubmit(trimmedReason)} disabled={!trimmedReason || isSubmitting}>
            {isSubmitting ? "Working..." : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SendResultDialog({
  result,
  onClose,
}: {
  result: SendOtcInvoiceQueueEntriesResult | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={result !== null} onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Invoice Send Result</DialogTitle>
          <DialogDescription>
            Sent {result?.sent_count ?? 0}; blocked {result?.blocked_count ?? 0}.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {result?.blocked.map((blocked: BlockedOtcInvoiceQueueEntry) => (
            <div key={blocked.queue_entry_id} className="rounded-md border p-3">
              <p className="font-medium">
                Contract {blocked.contract_number || "—"} / {blocked.amendment_type ? getAmendmentTypeDisplay(blocked.amendment_type) : "Initial Contract"} / {blocked.customer_names.join(", ") || "Unknown customer"}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                {blocked.reasons.map((reason: OtcInvoiceSendBlockingReason, index: number) => (
                  <li key={`${reason.code}-${index}`}>{reason.message} ({prettifyCode(reason.code)})</li>
                ))}
              </ul>
            </div>
          ))}
          {!result?.blocked?.length && (
            <div className="rounded-md border p-3">
              <p className="">
                No failures. Sent {result?.sent_count ?? 0}; blocked {result?.blocked_count ?? 0}.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
